import { createBrowserRouter, Navigate } from 'react-router-dom';

import { AppLayout } from './components/AppLayout';
import { RequireAccount, RequireCharacter } from './components/guards';
import { RouteError } from './components/RouteError';
import { CharacterSelect } from './views/CharacterSelect';
import { AccountView } from './views/AccountView';
import { ProgressView } from './views/ProgressView';
import { ProgressLayout } from './views/ProgressLayout';
import { ProgressConversations } from './views/ProgressConversations';
import { ResetView, VerifyView } from './views/LinkView';
import { SessionGate } from './views/SessionGate';
import { SessionView } from './views/SessionView';
import { SignupView } from './views/SignupView';
import { AccountProfile } from './views/AccountProfile';
import { AccountTutor } from './views/AccountTutor';
import { AccountHistory } from './views/AccountHistory';
import { AccountSettings } from './views/AccountSettings';
import { PracticeHubScreen } from './views/PracticeHubScreen';
import { PracticeItemDetail } from './views/PracticeItemDetail';
import { VocabularyDetail } from './views/VocabularyDetail';
import { BookShelf } from './views/BookShelf';
import { ScrapbookView } from './views/ScrapbookView';
import { LessonLibrary } from './views/LessonLibrary';
import { LessonSetView } from './views/LessonSetView';
import { LessonPlayer } from './views/LessonPlayer';
// Explicit extension: `StoryReader.tsx` and `storyReader.ts` (its stepping
// logic, `views/storyReader.test.ts`) share a directory and differ only in
// case, which a case-insensitive filesystem (macOS) makes tsc mis-resolve a
// bare `'./views/StoryReader'` to the wrong file.
import { StoryReader } from './views/StoryReader.tsx';
import { ListenWrite } from './views/ListenWrite.tsx';
import { SessionProvider } from './session/SessionProvider';
import type { PageWidth } from './components/pageWidth';

/**
 * The route table.
 *
 * `createBrowserRouter` rather than `<BrowserRouter><Routes>` for two reasons
 * that matter here: the table is plain data, and every route can carry an
 * `errorElement`. This app has never had an error boundary — a failed query
 * blanks the page with no message, which is indistinguishable from the bundle
 * failing to run, a failure this project has already shipped once.
 *
 * No loaders. Relay owns data fetching through `useLazyLoadQuery`; a loader
 * would be a second cache racing the first.
 *
 * `errorElement` on the layout route is not enough by itself: react-router
 * unmounts the nearest ancestor that carries one, so an error on `/history`
 * would take `AppLayout` — nav included — down with it, leaving no way back
 * except the browser's Back button. Every leaf route below repeats
 * `errorElement`, so a failed screen is replaced in place under a nav that
 * stays mounted and clickable; the layout's own copy remains as the backstop
 * for an error `AppLayout` itself throws (`ThemeAttribute`'s query, say).
 */
/**
 * The screens that hold pictures, tiles or the conversation beside its tutor
 * (`pageWidth.ts`); every other route is a reading column by default.
 */
const WIDE: { width: PageWidth } = { width: 'wide' };

export const router = createBrowserRouter([
  {
    element: <AppLayout />,
    errorElement: <RouteError />,
    children: [
      {
        element: <RequireAccount />,
        children: [
          {
            element: <RequireCharacter />,
            children: [
              // The conversation is the app, and `/session` is where it
              // lives. `6d54852` once restructured `/account` into nested
              // routes and took this entry with it, leaving `SessionView`
              // imported but never rendered -- so esbuild tree-shook it, and
              // with it the whole audio subsystem. The symptom was not a
              // missing screen but a bundle with no `AudioContext` in it at
              // all. `SessionProvider` is now that reachability edge: it
              // imports the audio machine, and it is a route element.
              {
                path: '/',
                element: <Navigate to="/session" replace />,
                errorElement: <RouteError />,
              },
              {
                // A layout route with no screen of its own. It holds the
                // session's identity, credentials and audio machine so that
                // the start screen and the conversation -- now two URLs --
                // can hand one live session between them: `startSession`
                // answers while `SessionGate` is still mounted, and its
                // single-use audio token would not survive the navigation.
                //
                // `index` decides rather than redirects to a `/session/start`
                // of its own. One fewer URL, and a learner with a conversation
                // running never sees a start screen flash before being put
                // back into it.
                path: '/session',
                element: <SessionProvider />,
                errorElement: <RouteError />,
                children: [
                  { index: true, element: <SessionGate />, errorElement: <RouteError />, handle: WIDE },
                  { path: ':sessionId', element: <SessionView />, errorElement: <RouteError />, handle: WIDE },
                ],
              },
              {
                // `/account`, not a pathless wrapper. Without a path of its
                // own this route's `index` child also matched `/` and won it
                // from the conversation -- `/` rendered the account screen --
                // and its `history` child shadowed the sibling `/history`, so
                // `ProgressView` was unreachable too. One missing path, two
                // screens lost.
                path: '/account',
                element: <AccountView />,
                errorElement: <RouteError />,
                children: [
                  { index: true, element: <Navigate to="profile" replace /> },
                  {
                    path: 'profile',
                    element: <AccountProfile />,
                    errorElement: <RouteError />,
                  },
                  {
                    path: 'tutor',
                    element: <AccountTutor />,
                    errorElement: <RouteError />,
                  },
                  {
                    path: 'history',
                    element: <AccountHistory />,
                    errorElement: <RouteError />,
                  },
                  {
                    path: 'settings',
                    element: <AccountSettings />,
                    errorElement: <RouteError />,
                  },
                ],
              },
              // Behind the character, unlike `/history` and `/practice`: the
              // server holds a learner with no character to the kid shelf
              // while the client's `registerFor` draws the adult reader, so
              // without this guard that learner saw the tales in the adult
              // look (PRD amendment "what phase 1b shipped").
              {
                path: '/books',
                element: <BookShelf />,
                errorElement: <RouteError />,
                handle: WIDE,
              },
              {
                path: '/books/:bookId',
                element: <StoryReader />,
                errorElement: <RouteError />,
                handle: WIDE,
              },
              // Adult only (listen & write design §1): `ListenWrite` sends
              // the kid register back to the shelf. Behind the character for
              // `/books`' reason, since the register comes from it.
              {
                path: '/books/:bookId/listen/:firstPage',
                element: <ListenWrite />,
                errorElement: <RouteError />,
              },
              // Adult only (lesson library PRD §8): each screen sends the kid
              // register to the shelf. Behind the character for `/books`'
              // reason, since the register comes from it.
              { path: '/lessons', element: <LessonLibrary />, errorElement: <RouteError /> },
              { path: '/lessons/play/:lessonId', element: <LessonPlayer />, errorElement: <RouteError /> },
              { path: '/lessons/:setId', element: <LessonSetView />, errorElement: <RouteError /> },
              // Behind the character for `/books`' reason: the register comes
              // from it, and the scrapbook is kid-register only (kid memory
              // design §3.3).
              {
                path: '/scrapbook',
                element: <ScrapbookView />,
                errorElement: <RouteError />,
              },
            ],
          },
          {
            path: '/character',
            element: <CharacterSelect />,
            errorElement: <RouteError />,
          },
          {
            // Still `/history`, so the nav and every bookmark keep working;
            // only the conversation list moved, to a tab of its own.
            path: '/history',
            element: <ProgressLayout />,
            errorElement: <RouteError />,
            children: [
              { index: true, element: <ProgressView />, errorElement: <RouteError /> },
              { path: 'conversations', element: <ProgressConversations />, errorElement: <RouteError /> },
            ],
          },
          {
            path: '/practice',
            element: <PracticeHubScreen />,
            errorElement: <RouteError />,
            handle: WIDE,
          },
          {
            path: '/practice/:itemId',
            element: <PracticeItemDetail />,
            errorElement: <RouteError />,
          },
          {
            // Before any wildcard could claim it, and more specific than
            // `/practice/:itemId` above, which react-router ranks correctly
            // regardless of order: a static segment outranks a dynamic one.
            path: '/practice/word/:itemId',
            element: <VocabularyDetail />,
            errorElement: <RouteError />,
          },
        ],
      },
    ],
  },
  { path: '/signup', element: <SignupView />, errorElement: <RouteError /> },
  { path: '/verify', element: <VerifyView />, errorElement: <RouteError /> },
  { path: '/reset', element: <ResetView />, errorElement: <RouteError /> },
  { path: '*', element: <Navigate to="/session" replace /> },
]);
